"use client"

import { useBandHudOverlay } from "./BandHudOverlayChannel"
import { useCapabilityHoverSwap } from "../hud/blocks/capability/capabilityHoverSwap"
import {
  UnifiedCapabilityPointPanel,
  UnifiedTakeoverRiskPanel,
  useUnifiedHudPanels,
} from "../hud/blocks/panels/unifiedHoverPanels"
import { BandDangerLabel } from "./RedAboveLossOfControlOverlay"
import { GraphPointHoverTooltip } from "./GraphPointHoverTooltip"
import { RiskSpaceExplorerOverlay, RiskTedGuideOverlay } from "./RiskFoomPreviewOverlay"

// In-flow layer that paints the small hovered-band text annotations (the danger
// label and the point hover tooltip) ABOVE the globe. It is rendered as a
// sibling of the globe, after it, pinned to the exact same box as the graph —
// so it both wins the stacking order against the globe and scrolls in perfect
// lockstep with the graph (plain in-flow DOM, no portal / fixed / re-measure).
//
// The large highlight raster does NOT render here: it paints BELOW the globe
// (see BelowGlobeBandHudOverlay) so the globe sits on top of the band, while
// these text annotations stay readable above it.
//
// The payloads carry graph-local coordinates (origin = graph container's
// top-left, which is this layer's own origin), so they map straight to
// left/top with no getBoundingClientRect.
export const AboveGlobeBandHudOverlay = () => {
  const { label, pointTooltip, riskTedGuide, riskSpaceGuide } = useBandHudOverlay()
  // Inside a <CapabilityHoverSwap> the point hover renders its info in-canvas
  // via CapabilityHoverSwapTarget, so the floating info box is suppressed
  // here — the on-line dot and guide line stay. Inside a <UnifiedHudPanels>
  // (the unified-card tab) both the point tooltip and the band danger label
  // render as the unified panel cards instead.
  const capabilitySwap = useCapabilityHoverSwap()
  const unifiedPanels = useUnifiedHudPanels()

  return (
    <>
      {label && (unifiedPanels ? (
        <UnifiedTakeoverRiskPanel label={label} />
      ) : (
        <BandDangerLabel
          anchor={label.anchor}
          label={label.text}
          opacity={label.opacity}
        />
      ))}
      {pointTooltip && (
        <>
          <GraphPointHoverTooltip
            point={pointTooltip}
            hideInfoBox={Boolean(capabilitySwap) || Boolean(unifiedPanels)}
          />
          {unifiedPanels && <UnifiedCapabilityPointPanel point={pointTooltip} />}
        </>
      )}
      {/* The tab-26 TED guide paints above the earth: when the cursor's
          column passes behind the globe the line and label stay readable on
          top of it (hovering the earth itself ends the graph hover, so the
          guide clears there). */}
      {riskTedGuide && <RiskTedGuideOverlay {...riskTedGuide} />}
      {/* The above-globe risk-space explorer (picks tab 6): square, readout,
          drop line, and red axis year on top of the earth — pairs with the
          earth's own field-hover fade. */}
      {riskSpaceGuide && (
        <RiskSpaceExplorerOverlay {...riskSpaceGuide} timelineGuide />
      )}
    </>
  )
}
